import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { getFeeRequests, confirmFeeSent, confirmFeeSentBulk } from "../../api/deliveryCompanyService";
import { formatCurrency, formatDate } from "../Admin/constants";

const VIEW_TABS = [
  { value: "new", label: "New Requests" },
  { value: "sent", label: "Sent - Awaiting Confirmation" },
  { value: "history", label: "History" },
];

const SOURCE_LABELS = {
  Store: "Store",
  Deliveries: "My Deliveries",
};

export default function Fees() {
  const [view, setView] = useState("new");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busySettlementId, setBusySettlementId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  function loadRows() {
    setLoading(true);
    getFeeRequests({ status: view === "history" ? "2" : "1" })
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load fee requests. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(loadRows, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayRows = useMemo(() => {
    if (view === "new") return rows.filter((r) => !r.companyConfirmedSentAt);
    if (view === "sent") return rows.filter((r) => !!r.companyConfirmedSentAt);
    return rows;
  }, [rows, view]);

  const totalAmount = useMemo(
    () => displayRows.reduce((sum, r) => sum + (r.amount || 0), 0),
    [displayRows]
  );

  async function handleConfirmSent(row) {
    setBusySettlementId(row.commissionSettlementId);
    try {
      await confirmFeeSent(row.commissionSettlementId);
      loadRows();
    } catch {
      setError("Could not confirm this as sent.");
    } finally {
      setBusySettlementId(null);
    }
  }

  async function handleSendAll() {
    const ids = displayRows.map((r) => r.commissionSettlementId);
    if (ids.length === 0) return;

    setSendingAll(true);
    try {
      await confirmFeeSentBulk(ids);
      loadRows();
    } catch {
      setError("Could not mark all requests as sent.");
    } finally {
      setSendingAll(false);
    }
  }

  const columns = useMemo(() => {
    const base = [
      {
        field: "source",
        headerName: "From",
        width: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={SOURCE_LABELS[params.value] || params.value}
            color={params.value === "Store" ? "primary" : "secondary"}
          />
        ),
      },
      { field: "label", headerName: "Store / Source", flex: 1, minWidth: 180 },
      { field: "periodStart", headerName: "From", width: 150, valueFormatter: (v) => formatDate(v) },
      { field: "periodEnd", headerName: "To", width: 150, valueFormatter: (v) => formatDate(v) },
      { field: "orderCount", headerName: "Orders", width: 100 },
      { field: "amount", headerName: "Amount", width: 140, valueFormatter: (v) => formatCurrency(v) },
    ];

    if (view === "new") {
      return [
        ...base,
        { field: "generatedAt", headerName: "Requested At", width: 170, valueFormatter: (v) => formatDate(v) },
        {
          field: "sent",
          headerName: "",
          width: 150,
          sortable: false,
          renderCell: (params) => (
            <Button
              size="small"
              variant="contained"
              disabled={busySettlementId === params.row.commissionSettlementId}
              onClick={() => handleConfirmSent(params.row)}
            >
              I Sent It
            </Button>
          ),
        },
      ];
    }

    if (view === "sent") {
      return [
        ...base,
        {
          field: "companyConfirmedSentAt",
          headerName: "Sent At",
          width: 170,
          valueFormatter: (v) => formatDate(v),
        },
        {
          field: "status",
          headerName: "",
          width: 200,
          sortable: false,
          renderCell: () => <Chip size="small" color="success" label="Sent - awaiting confirmation" />,
        },
      ];
    }

    return [...base, { field: "paidAt", headerName: "Received At", width: 170, valueFormatter: (v) => formatDate(v) }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, busySettlementId]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Fees
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        What the platform has requested from you - your own delivery commission, plus product fees for
        any Prime store you service. Once you send the money, click "I Sent It" so the platform knows to
        expect it.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Tabs value={view} onChange={(e, v) => setView(v)}>
          {VIEW_TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>

        {view === "new" && (
          <Button
            variant="contained"
            disabled={sendingAll || displayRows.length === 0}
            onClick={handleSendAll}
          >
            I Sent All ({displayRows.length})
          </Button>
        )}
      </Box>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={displayRows}
          getRowId={(row) => row.commissionSettlementId}
          columns={columns}
          loading={loading}
          hideFooter
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
        {(view === "new" || view === "sent") && !loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              px: 2,
              py: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2">
              Total: {formatCurrency(totalAmount)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
